import { CSSProperties } from 'react';
import { CoworkTask, OverlayStyle } from './types';

export function withAlpha(color: string, alpha: number): string {
  const value = String(color || '').trim();
  const a = Math.max(0, Math.min(1, alpha));

  const hex = value.replace('#', '');
  if (/^[0-9a-f]{6}$/i.test(hex)) {
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  if (/^[0-9a-f]{3}$/i.test(hex)) {
    const r = parseInt(hex[0] + hex[0], 16);
    const g = parseInt(hex[1] + hex[1], 16);
    const b = parseInt(hex[2] + hex[2], 16);
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  const rgbMatch = value.match(/rgba?\(([^)]+)\)/i);
  if (rgbMatch) {
    const [r, g, b] = rgbMatch[1].split(',').map((n) => parseFloat(n));
    return `rgba(${r}, ${g}, ${b}, ${a})`;
  }
  return value;
}

/** Radio efectivo del panel según la forma elegida. */
export function panelRadius(style: OverlayStyle): number {
  switch (style.panelShape) {
    case 'pill':
      return Math.max(style.borderRadius || 0, 34);
    case 'square':
    case 'cut':
    case 'notch':
      return 0;
    default:
      return style.borderRadius || 0;
  }
}

/** Radio en CSS: algunas formas usan radios distintos por esquina. */
function panelRadiusCss(style: OverlayStyle): string | number {
  if (style.panelShape === 'leaf') {
    const big = Math.max(14, style.borderRadius || 24);
    return `${big}px ${Math.round(big * 0.18)}px ${big}px ${Math.round(big * 0.18)}px`;
  }
  return panelRadius(style);
}

/** Recorte de esquinas para las formas biseladas. */
function panelClipPath(style: OverlayStyle): string | undefined {
  const cut = Math.max(8, style.borderRadius || 14);
  if (style.panelShape === 'cut') {
    return `polygon(${cut}px 0, 100% 0, 100% calc(100% - ${cut}px), calc(100% - ${cut}px) 100%, 0 100%, 0 ${cut}px)`;
  }
  if (style.panelShape === 'notch') {
    return `polygon(0 0, calc(100% - ${cut}px) 0, 100% ${cut}px, 100% 100%, 0 100%)`;
  }
  return undefined;
}

/** Alineación horizontal en flex a partir de la alineación del preset. */
export function justifyFor(align: string): 'flex-start' | 'center' | 'flex-end' {
  if (align === 'right') return 'flex-end';
  if (align === 'center') return 'center';
  return 'flex-start';
}

/** Contenedor base de cualquier bloque del overlay. */
export function surfaceStyle(style: OverlayStyle, extra: CSSProperties = {}): CSSProperties {
  const clipPath = panelClipPath(style);
  const shadowStyle = style.shadowStyle || 'soft';
  const shadows: string[] = [];

  // Con clip-path las sombras exteriores se recortan, así que van hacia adentro.
  const inset = !!clipPath;

  if (style.shadowStrength > 0 && shadowStyle !== 'none') {
    if (shadowStyle === 'hard') {
      const offset = Math.max(2, Math.round(style.shadowStrength / 10));
      shadows.push(`${inset ? 'inset ' : ''}${offset}px ${offset}px 0 ${withAlpha(style.borderColor || '#000000', 1)}`);
    } else if (inset) {
      shadows.push(`inset 0 0 ${Math.round(style.shadowStrength / 1.6)}px rgba(0, 0, 0, ${(style.shadowStrength / 100) * 0.4})`);
    } else {
      shadows.push(`0 ${Math.round(style.shadowStrength / 5)}px ${Math.round(style.shadowStrength / 2)}px rgba(0, 0, 0, ${(style.shadowStrength / 100) * 0.55})`);
    }
  }

  if (style.glowStrength > 0) {
    const glow = withAlpha(style.accentColor, (style.glowStrength / 100) * 0.5);
    shadows.push(`${inset ? 'inset ' : ''}0 0 ${Math.round(style.glowStrength / 2)}px ${glow}`);
  }

  return {
    fontFamily: style.bodyFontFamily || style.fontFamily,
    color: style.textColor,
    background: style.backgroundOpacity > 0
      ? withAlpha(style.backgroundColor, style.backgroundOpacity / 100)
      : 'transparent',
    border: style.borderWidth > 0 ? `${style.borderWidth}px solid ${style.borderColor}` : 'none',
    borderRadius: panelRadiusCss(style),
    clipPath,
    backdropFilter: style.backdropBlur > 0 ? `blur(${style.backdropBlur}px)` : undefined,
    WebkitBackdropFilter: style.backdropBlur > 0 ? `blur(${style.backdropBlur}px)` : undefined,
    padding: style.padding,
    boxShadow: shadows.length ? shadows.join(', ') : 'none',
    display: 'flex',
    flexDirection: 'column',
    gap: style.gap,
    textAlign: style.align,
    alignItems: style.align === 'center' ? 'center' : style.align === 'right' ? 'flex-end' : 'flex-start',
    ...extra,
  } as CSSProperties;
}

/** True cuando la forma del panel no admite esquinas redondeadas. */
export function isSharpShape(style: OverlayStyle): boolean {
  return style.panelShape === 'square' || style.panelShape === 'cut' || style.panelShape === 'notch';
}

/** Barra o franja de acento del panel, si el preset la usa. */
export function accentBarStyle(style: OverlayStyle): CSSProperties | null {
  if (!style.accentBar) return null;
  const thickness = Math.max(1, style.accentBarThickness || 4);

  if (style.accentBarPosition === 'top') {
    return {
      alignSelf: 'stretch',
      height: thickness + 2,
      background: style.accentColor,
      borderRadius: isSharpShape(style) ? 0 : 999,
      flexShrink: 0,
    };
  }
  return {
    position: 'absolute',
    left: 0,
    top: style.panelShape === 'pill' ? '12%' : 0,
    bottom: style.panelShape === 'pill' ? '12%' : 0,
    width: thickness,
    background: style.accentColor,
    borderRadius: isSharpShape(style) ? 0 : 999,
  };
}

/** Sangría interna del contenido, separada de la franja de acento. */
export function contentIndentFor(style: OverlayStyle): number {
  const bar = style.accentBar && style.accentBarPosition !== 'top'
    ? Math.max(1, style.accentBarThickness || 4) + 8
    : 0;
  return bar + Math.max(0, style.contentIndent || 0);
}

/** Decoración por fila de lista según rowStyle. */
export function rowStyleFor(style: OverlayStyle, index: number, extra: CSSProperties = {}): CSSProperties {
  const base: CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: Math.max(6, Math.round(style.gap * 0.55)),
    width: '100%',
  };

  const radius = style.panelShape === 'pill'
    ? 999
    : isSharpShape(style)
      ? 0
      : Math.max(6, Math.round((style.borderRadius || 12) * 0.55));

  switch (style.rowStyle) {
    case 'striped':
      return {
        ...base,
        background: index % 2 === 1 ? withAlpha(style.textColor, 0.07) : 'transparent',
        padding: `4px ${Math.max(6, Math.round(style.padding * 0.3))}px`,
        borderRadius: radius,
        ...extra,
      };
    case 'card':
      return {
        ...base,
        background: withAlpha(style.textColor, 0.08),
        padding: `${Math.max(5, Math.round(style.padding * 0.3))}px ${Math.max(9, Math.round(style.padding * 0.55))}px`,
        borderRadius: radius,
        ...extra,
      };
    case 'underline':
      return {
        ...base,
        paddingBottom: Math.max(4, Math.round(style.gap * 0.45)),
        borderBottom: `1px solid ${withAlpha(style.textColor, 0.16)}`,
        ...extra,
      };
    default:
      return { ...base, ...extra };
  }
}

const MARKERS: Record<string, { focus: string; pending: string; done: string }> = {
  dot: { focus: '●', pending: '○', done: '✓' },
  check: { focus: '▸', pending: '☐', done: '☑' },
  bracket: { focus: '[»]', pending: '[ ]', done: '[x]' },
  arrow: { focus: '➜', pending: '›', done: '✓' },
  dash: { focus: '—', pending: '–', done: '✓' },
  square: { focus: '◼', pending: '◻', done: '✔' },
  number: { focus: '', pending: '', done: '✓' },
};

/**
 * El número fijo con el que el viewer llama a su tarea en el chat. Las tareas de Euge
 * no lo usan (las maneja desde el panel), así que ahí seguimos con la posición.
 */
export function taskNumber(task: Pick<CoworkTask, 'number'>, index: number): number {
  return task.number && task.number > 0 ? task.number : index + 1;
}

/** Marcador de una tarea: distingue foco, pendiente y completada. */
export function taskMarker(
  style: OverlayStyle,
  task: Pick<CoworkTask, 'status' | 'focused' | 'number'>,
  index: number,
): string {
  const set = MARKERS[style.markerStyle] || MARKERS.dot;
  if (style.markerStyle === 'number') {
    const label = taskNumber(task, index);
    // La completada mantiene su número: es el que hace falta para !deshacer.
    return task.status === 'completed' ? `${label} ${set.done}` : `${label}.`;
  }
  if (task.status === 'completed') return set.done;
  if (task.focused) return set.focus;
  return set.pending;
}

export function labelStyle(style: OverlayStyle, extra: CSSProperties = {}): CSSProperties {
  return {
    fontFamily: style.fontFamily,
    fontSize: style.titleFontSize,
    fontWeight: style.fontWeight,
    letterSpacing: style.letterSpacing,
    textTransform: style.uppercaseLabels ? 'uppercase' : 'none',
    color: style.accentColor,
    lineHeight: 1.2,
    textShadow: style.textShadow ? '0 2px 8px rgba(0, 0, 0, 0.75)' : undefined,
    ...extra,
  };
}

export function bodyStyle(style: OverlayStyle, extra: CSSProperties = {}): CSSProperties {
  return {
    fontFamily: style.bodyFontFamily || style.fontFamily,
    fontSize: style.bodyFontSize,
    color: style.textColor,
    lineHeight: 1.35,
    textShadow: style.textShadow ? '0 2px 8px rgba(0, 0, 0, 0.75)' : undefined,
    ...extra,
  };
}

export function mutedStyle(style: OverlayStyle, extra: CSSProperties = {}): CSSProperties {
  return bodyStyle(style, { color: style.mutedColor, fontSize: Math.round(style.bodyFontSize * 0.8), ...extra });
}

/** Línea divisoria debajo de los títulos, si el preset la usa. */
export function dividerStyle(style: OverlayStyle): CSSProperties {
  return {
    alignSelf: 'stretch',
    height: 1,
    background: withAlpha(style.accentColor, 0.35),
    marginTop: -Math.round(style.gap * 0.4),
    flexShrink: 0,
  };
}

/** Duración de animación ajustada por el control de velocidad (100 = normal). */
export function animMs(style: OverlayStyle, baseMs: number): number {
  if (!style.animationsEnabled) return 0;
  const speed = Math.max(10, style.animationSpeed || 100);
  return Math.round(baseMs * (100 / speed));
}

export function formatClock(ms: number, showSeconds: boolean): string {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (!showSeconds) {
    const roundedMinutes = Math.ceil(totalSeconds / 60);
    if (hours > 0) return `${hours}:${String(roundedMinutes % 60).padStart(2, '0')}`;
    return String(roundedMinutes);
  }

  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatDuration(ms: number): string {
  const totalMinutes = Math.round(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${minutes} min`;
}
